import { useTranslation } from 'react-i18next';

import { Font, Image, View } from '@react-pdf/renderer';
import { BUSINESS_PLAN_TYPES, IBusinessToolType } from 'constants/businessPlan';
import { PDFPage } from 'PDF/components/Page';
import { ITextItem, PdfContentText } from 'PDF/components/PdfContentText';
import { SmallHeading } from 'PDF/components/SmallHeading';
import { SubChapterHeading } from 'PDF/components/SubChapterHeading';
import { PDF_SECTIONS } from 'PDF/constants/sections';
import { Noto } from 'PDF/fonts';
import { prepareSectionId } from 'PDF/helpers/pdfMapping';
import { getSectionInfo } from 'PDF/helpers/sections';
import {
  getManagementAndOrganizationChapterNames,
  ManagementAndOrganizationKeys,
  ManagementAndOrganizationSectionAvailability,
} from 'PDF/types/pdfSections';

import { getSubSectionHeading } from 'pages/BusinessToolPage/PdfPreview/utils';

import { SectionCoverPagePdf } from '../SectionCoverPage';
import { KeyPersonnelProfilesTable } from './components/KeyPersonnelProfilesTable';
import { ProfilesTable } from './components/ProfilesTable';
import { IManagementAndOrganizationPdfData } from './types';
import { getNextSectionIndex } from '../TableOfContents/helpers';

Font.getRegisteredFonts();
Font.register(Noto);

export interface IManagementAndOrganizationPdfProps {
  magazineItems: ITextItem[][];
  data: IManagementAndOrganizationPdfData;
  businessPlanType: IBusinessToolType;
  sectionPicture?: string;
  isStandardOrLightTariff: boolean;
  isExamplePage: boolean;
}

export const ManagementAndOrganizationPdf = (
  props: IManagementAndOrganizationPdfProps & {
    availability: ManagementAndOrganizationSectionAvailability;
    isPreviewMode?: boolean;
    sectionNumber: string;
  },
) => {
  const {
    magazineItems,
    data,
    businessPlanType,
    sectionPicture,
    availability,
    isPreviewMode = false,
    sectionNumber,
    isStandardOrLightTariff,
    isExamplePage
  } = props;

  const {t} = useTranslation()

  const { sectionHeading } = getSectionInfo(businessPlanType, PDF_SECTIONS.MANAGEMENT_AND_ORGANIZATION, isExamplePage);
  const getSubHeading = getSubSectionHeading(sectionNumber);
  const getSubChapterIndex = getNextSectionIndex();
  const MANAGEMENT_AND_ORGANIZATION_CHAPTER_NAMES = getManagementAndOrganizationChapterNames(isExamplePage);
  const isInvestor = businessPlanType === BUSINESS_PLAN_TYPES.FOR_INVESTORS;
  const subChapterNames = (Object.keys(availability) as ManagementAndOrganizationKeys[])
    .filter((key) => !!availability[key])
    .map(
      (key) => `${Number(sectionNumber)}.${getSubChapterIndex()}. ${MANAGEMENT_AND_ORGANIZATION_CHAPTER_NAMES[key]}`,
    );
  const isSectionEmpty = Object.values(availability).every((value) => !value);
  const isCoverPageEmpty = isSectionEmpty && isPreviewMode;

  if (!data) {
    return (
      <SectionCoverPagePdf
        header={sectionHeading}
        sectionNumber={sectionNumber}
        sectionId={prepareSectionId(businessPlanType, PDF_SECTIONS.MANAGEMENT_AND_ORGANIZATION)}
        background={sectionPicture ?? ''}
        highlightSettings={{ position: 1, variant: 'after' }}
        subChapterNames={subChapterNames}
        isEmpty={isCoverPageEmpty}
        isStandardOrLightTariff={isStandardOrLightTariff}
        isExamplePage={isExamplePage}
      />
    );
  }

  const { keyPersonnelProfiles, profiles, organizationalStructureImg } = data;

  return (
    <>
      {(!isSectionEmpty || isCoverPageEmpty) && (
        <SectionCoverPagePdf
          header={sectionHeading}
          sectionNumber={sectionNumber}
          sectionId={prepareSectionId(businessPlanType, PDF_SECTIONS.MANAGEMENT_AND_ORGANIZATION)}
          background={sectionPicture ?? ''}
          highlightSettings={{ position: 1, variant: 'after' }}
          subChapterNames={subChapterNames}
          isEmpty={isCoverPageEmpty}
          isStandardOrLightTariff={isStandardOrLightTariff}
          isExamplePage={isExamplePage}
        />
      )}
      {!isSectionEmpty && (
        <PDFPage
          shouldShowHeaderAndFooter
          sectionNumber={sectionNumber}
          sectionTitle={sectionHeading}
          withEmptySpaceImage
          isStandardOrLightTariff={isStandardOrLightTariff}
        >
          {availability.management && (
            <>
              <SubChapterHeading
                id="management"
                heading={getSubHeading(MANAGEMENT_AND_ORGANIZATION_CHAPTER_NAMES.management)}
              />
              <PdfContentText items={magazineItems[0]} />
              {keyPersonnelProfiles.length > 0 && <KeyPersonnelProfilesTable items={keyPersonnelProfiles} isExamplePage={isExamplePage}/>}
            </>
          )}
          {availability.partners && (
            <>
              <View minPresenceAhead={profiles.length > 0 ? 250 : 0} />
              {!profiles.length && (
                <SubChapterHeading
                  id="partners"
                  heading={getSubHeading(MANAGEMENT_AND_ORGANIZATION_CHAPTER_NAMES.partners)}
                />
              )}
              {profiles.length > 0 && (
                <ProfilesTable
                  items={profiles}
                  isExamplePage={isExamplePage}
                  heading={
                    <>
                      {profiles.length > 0 && (
                        <SubChapterHeading
                          id="partners"
                          heading={getSubHeading(MANAGEMENT_AND_ORGANIZATION_CHAPTER_NAMES.partners)}
                        />
                      )}

                      <SmallHeading heading={isExamplePage ? "Profiles" : t('pdfPages.mappings.managementAndOrganization.profiles')} />
                    </>
                  }
                />
              )}
              <PdfContentText items={magazineItems[1]} />
            </>
          )}
          {availability.staff && (
            <>
              <SubChapterHeading id="staff" heading={getSubHeading(MANAGEMENT_AND_ORGANIZATION_CHAPTER_NAMES.staff)} />
              <PdfContentText
                items={magazineItems[!isInvestor ? 2 : 1]}
                withoutBottomMargin={!organizationalStructureImg}
              />
              {organizationalStructureImg && (
                <View wrap={false}>
                  <View style={{ marginBottom: 20 }}>
                    <SmallHeading heading={isExamplePage ? "Organizational Structure" : t('pdfPages.mappings.managementAndOrganization.orgStructure')} />
                    <Image
                      src={organizationalStructureImg}
                      style={{
                        margin: '0 auto',
                      }}
                    />
                  </View>
                </View>
              )}
              <PdfContentText items={magazineItems[!isInvestor ? 3 : 2]} />
            </>
          )}
        </PDFPage>
      )}
    </>
  );
};
